import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";

export default function SyndicateDetail() {
  const { id } = useParams();
  const [syn, setSyn] = useState(null);
  const [stats, setStats] = useState({ pod: 0, speeder: 0, pod_wins: 0, speeder_wins: 0 });
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState("");

  useEffect(() => {
    api.get(`/syndicates/${id}`).then((r) => setSyn(r.data));
    api.get("/seasons").then((res) => {
      setSeasons(res.data);
      const active = res.data.find(x => x.is_active);
      if (active) setSelectedSeasonId(active.id);
      else if (res.data.length > 0) setSelectedSeasonId(res.data[0].id);
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!selectedSeasonId) return;
    const podUrl = `/standings/syndicates?discipline=pod&season_id=${selectedSeasonId}`;
    const speederUrl = `/standings/syndicates?discipline=speeder&season_id=${selectedSeasonId}`;
    
    Promise.all([
      api.get(podUrl),
      api.get(speederUrl),
    ]).then(([a, b]) => {
      const p = a.data.find((x) => x.syndicate.id === id);
      const s = b.data.find((x) => x.syndicate.id === id);
      setStats({
        pod: p?.points || 0,
        speeder: s?.points || 0,
        pod_wins: p?.wins || 0,
        speeder_wins: s?.wins || 0
      });
    }).catch(() => {});
  }, [id, selectedSeasonId]);

  if (!syn) return <div className="p-16 small-label">Loading…</div>;

  const logoUrl = syn.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${syn.logo_url}` : null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
      <div className="rtrl-card carbon-bg p-8 relative overflow-hidden" style={{ borderLeft: `6px solid ${syn.color}` }}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {logoUrl && (
              <img src={logoUrl} alt={syn.name} className="w-16 h-16 rounded-full object-contain bg-void/50 border border-surface-line shadow-[0_0_15px_rgba(255,255,255,0.05)] shrink-0" />
            )}
            <div>
              <div className="small-label" style={{ color: syn.color }}>{syn.tag}</div>
              <h1 className="font-display uppercase text-6xl text-white mt-2 leading-none">{syn.name}</h1>
            </div>
          </div>
          
          {seasons.length > 0 && (
            <div className="flex items-center gap-2 relative z-10 bg-void/50 p-2 rounded border border-surface-line/40">
              <span className="small-label text-rtrl-silver font-semibold">Stats Season:</span>
              <select
                className="rtrl-input w-48 text-sm"
                value={selectedSeasonId}
                onChange={(e) => setSelectedSeasonId(e.target.value)}
                data-testid="syndicate-season-select"
              >
                {seasons.map(s => (
                  <option key={s.id} value={s.id}>{s.name} {s.is_active ? "(Active)" : ""}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <p className="text-white/60 mt-3 max-w-3xl">{syn.bio}</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <Stat label="Pod Points" value={stats.pod} color="#FF6B00" />
          <Stat label="Speeder Points" value={stats.speeder} color="#00F0FF" />
          <Stat label="Pod Wins" value={stats.pod_wins} color="#FF6B00" />
          <Stat label="Speeder Wins" value={stats.speeder_wins} color="#00F0FF" />
        </div>
      </div>

      {/* Management / Leadership Section */}
      {(syn.owner || syn.gm) && (
        <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl">
          {syn.owner && (
            <div className="rtrl-card carbon-bg p-6 border border-rtrl-red/20 shadow-[0_0_15px_rgba(229,25,25,0.05)]" style={{ borderLeft: `4px solid ${syn.color}` }}>
              <div className="small-label text-rtrl-red uppercase tracking-widest font-mono-tech">Syndicate Owner</div>
              <div className="text-white font-body text-2xl font-bold mt-1">{syn.owner.display_name}</div>
              {syn.owner_racer?.epic_id && (
                <div className="text-xs text-rtrl-silver font-mono-tech mt-1 flex items-center gap-1.5">
                  Epic ID: {syn.owner_racer.epic_id}
                </div>
              )}
              {syn.owner_racer?.discord_username && (
                <div className="text-xs text-indigo-400 font-mono-tech mt-2 flex items-center gap-1.5 bg-indigo-500/5 px-2.5 py-1.5 rounded border border-indigo-500/10 w-fit">
                  <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
                  Discord: {syn.owner_racer.discord_username}
                </div>
              )}
            </div>
          )}
          {syn.gm && (
            <div className="rtrl-card carbon-bg p-6 border border-rtrl-red/20 shadow-[0_0_15px_rgba(229,25,25,0.05)]" style={{ borderLeft: `4px solid ${syn.color}` }}>
              <div className="small-label text-rtrl-red uppercase tracking-widest font-mono-tech">General Manager (GM)</div>
              <div className="text-white font-body text-2xl font-bold mt-1">{syn.gm.display_name}</div>
              {syn.gm_racer?.epic_id && (
                <div className="text-xs text-rtrl-silver font-mono-tech mt-1 flex items-center gap-1.5">
                  Epic ID: {syn.gm_racer.epic_id}
                </div>
              )}
              {syn.gm_racer?.discord_username && (
                <div className="text-xs text-indigo-400 font-mono-tech mt-2 flex items-center gap-1.5 bg-indigo-500/5 px-2.5 py-1.5 rounded border border-indigo-500/10 w-fit">
                  <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
                  Discord: {syn.gm_racer.discord_username}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <h2 className="font-display uppercase text-4xl text-white mt-16">Roster</h2>
      <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {syn.roster.map((r) => (
          <div key={r.id} className="border border-surface-line bg-surface p-4 hud-chamfer-sm">
            <div className="flex items-center justify-between">
              <div className="text-white font-body text-lg font-bold">{r.display_name}</div>
              <div className="font-mono-tech text-rtrl-red">#{r.number ?? "-"}</div>
            </div>
            <div className="small-label mt-1">{r.platform || "—"} · {r.region || "—"}</div>
            {r.discord_username && (
              <div className="text-xs text-indigo-400/80 font-mono-tech mt-1.5 truncate flex items-center gap-1">
                <span>👾</span> {r.discord_username}
              </div>
            )}
            <div className="mt-3 flex gap-2">
              {(r.discipline === "pod" || r.discipline === "both") && <span className="disc-chip-pod">Pod</span>}
              {(r.discipline === "speeder" || r.discipline === "both") && <span className="disc-chip-speeder">Speeder</span>}
              {r.status === "tc" && <span className="disc-chip-pod" style={{ color: "#FFD600", borderColor: "#FFD60066" }}>TC</span>}
            </div>
          </div>
        ))}
        {syn.roster.length === 0 && <div className="text-white/50 col-span-full py-4 italic">No racers on roster yet.</div>}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="border border-surface-line p-4 hud-chamfer-sm">
      <div className="small-label" style={{ color }}>{label}</div>
      <div className="font-display text-5xl text-white mt-1">{value}</div>
    </div>
  );
}
